import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useUnreadCountContext, useShop } from '../App';
import KioskMode from './KioskMode';

export default function Layout({ children }) {
  const { user, logout, kioskLogout, loginAs, showKioskReauth, setShowKioskReauth, isKioskSession, kioskDeadline, clearAuthToken } = useAuth();
  const { shops, currentShop, selectShop } = useShop();
  const [countdown, setCountdown] = useState('');
  const navigate = useNavigate();
  const { unreadCount } = useUnreadCountContext();
  const [menuOpen, setMenuOpen] = useState(false);
  const [shopMenuOpen, setShopMenuOpen] = useState(false);
  const [showKiosk, setShowKiosk] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleKioskLogout = async () => {
    await kioskLogout();
    navigate('/login');
  };

  const handleKioskLogin = (newUser) => {
    loginAs(newUser);
    setShowKiosk(false);
    setShowKioskReauth(false);
    window.location.reload();
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const isAdmin = user?.role === 'admin';

  // Countdown timer - updates every second
  useEffect(() => {
    if (!isKioskSession || !kioskDeadline) {
      setCountdown('');
      return;
    }

    const updateCountdown = () => {
      const remaining = Math.max(0, Math.floor((kioskDeadline - Date.now()) / 1000));
      const minutes = Math.floor(remaining / 60);
      const seconds = remaining % 60;
      setCountdown(`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [isKioskSession, kioskDeadline]);

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      <nav className="bg-white shadow-sm flex-shrink-0">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center gap-4">
              {/* Shop selector dropdown */}
              <div className="relative flex-shrink-0 flex items-center">
                <button
                  onClick={() => setShopMenuOpen(!shopMenuOpen)}
                  className="text-xl font-bold text-gray-800 hover:text-gray-600 focus:outline-none flex items-center gap-1"
                >
                  {currentShop?.shop_name || 'No Shop'}
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {shopMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShopMenuOpen(false)} />
                    <div className="absolute left-0 top-full mt-1 w-64 bg-white rounded-md shadow-lg z-20 border py-1 max-h-80 overflow-y-auto">
                      {shops.length === 0 ? (
                        <div className="px-4 py-2 text-sm text-gray-400">No shops configured</div>
                      ) : (
                        shops.map((shop) => (
                          <button
                            key={shop.id}
                            onClick={() => {
                              selectShop(shop.id);
                              setShopMenuOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${
                              currentShop?.id === shop.id ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700'
                            }`}
                          >
                            <span>{shop.shop_name}</span>
                            <span className="text-xs text-gray-400 font-mono ml-2">{shop.shop_id}</span>
                            {currentShop?.id === shop.id && (
                              <svg className="w-4 h-4 text-indigo-600 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                <Link to="/customers" className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium">
                  Customers
                </Link>
                <Link to="/transactions" className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium">
                  Transactions
                </Link>
                <Link to="/repairs" className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium">
                  Repairs
                </Link>
                <Link to="/custom-jobs" className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium">
                  Custom Jobs
                </Link>
                <Link to="/appraisals" className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium">
                  Appraisals
                </Link>
                <Link to="/messages" className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium">
                  Messages
                  {unreadCount > 0 && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-500 text-white">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isKioskSession && countdown && (
                <button
                  onClick={() => {
                    clearAuthToken();
                    setShowKiosk(true);
                  }}
                  className="flex flex-col items-center justify-center px-3 py-1 rounded-full border border-amber-200 bg-amber-50 hover:bg-amber-100 active:bg-amber-200 transition-colors cursor-pointer"
                >
                  <span className="text-xs font-semibold text-amber-700 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8V9z" />
                    </svg>
                    Lock
                  </span>
                  <span className="text-xs font-mono font-semibold text-amber-600">
                    {countdown}
                  </span>
                </button>
              )}
              <div className="relative">
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="flex items-center focus:outline-none"
                >
                  {isAdmin ? (
                    <div className="relative w-9 h-9">
                      <svg className="w-9 h-9 text-blue-500" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 2l8 3.5v5.5c0 5.5-3.5 9.5-8 11-4.5-1.5-8-5.5-8-11V5.5L12 2z" />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xs font-bold text-blue-500">{getInitials(user?.name)}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center">
                      <span className="text-sm font-bold text-white">{getInitials(user?.name)}</span>
                    </div>
                  )}
                </button>

                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50 border">
                    <button
                      onClick={() => {
                          setMenuOpen(false);
                          clearAuthToken();
                          setShowKiosk(true);
                        }}
                      className="block w-full text-left px-4 py-2 text-sm text-indigo-600 hover:bg-gray-100 font-medium"
                    >
                      Lock POS
                    </button>
                    <div className="border-t border-gray-100 my-1"></div>
                    {isAdmin && (
                      <>
                        <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Admin</div>
                        <Link
                          to="/"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setMenuOpen(false)}
                        >
                          Dashboard
                        </Link>
                        <Link
                          to="/users"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setMenuOpen(false)}
                        >
                          User Management
                        </Link>
                        <Link
                          to="/shops"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setMenuOpen(false)}
                        >
                          Shops
                        </Link>
                        <Link
                          to="/performance"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setMenuOpen(false)}
                        >
                          Performance
                        </Link>
                        <Link
                          to="/tax-reports"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setMenuOpen(false)}
                        >
                          Tax Reports
                        </Link>
                        <Link
                          to="/shoutout"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setMenuOpen(false)}
                        >
                          Shoutout
                        </Link>
                        <Link
                          to="/ip-whitelist"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setMenuOpen(false)}
                        >
                          Kiosk IP Whitelist
                        </Link>
                        <div className="border-t border-gray-100 my-1"></div>
                      </>
                    )}
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Account</div>
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setMenuOpen(false)}
                    >
                      Profile
                    </Link>
                    <div className="border-t border-gray-100 my-1"></div>
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        handleLogout();
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
      <main className="flex-1 overflow-hidden w-full py-1 sm:px-1 lg:px-8">
        {children}
      </main>

      {(showKiosk || showKioskReauth) && (
        <KioskMode
          onClose={handleKioskLogout}
          onLogin={handleKioskLogin}
          isReauth={showKioskReauth}
        />
      )}

    </div>
  );
}
